import React, { Fragment, useEffect, useState } from "react";
import { Collapse, List, ListItem } from "@mui/material";
import { useSelector } from "react-redux";
import { CustomIconButton, CustomTooltip, icon, IconComponent } from "../../../../../common/components";
import { Permissions, translate } from "../../../../../common/providers";
import styles from "./CompanyRow.module.css";

const CompanyRow = ({
	company,
	displayLeaderIcon = false,
	level = 0,
	selectedCompany,
	isProjectDirector,
	onBlock,
	onClickCompany,
	onEdit,
}) => {
	const myCompanyId = useSelector(({ context }) => context.company.id);
	const [collapsed, setCollapsed] = useState(true);
	const [isSelected, setIsSelected] = useState(false);
	const [isMyCompany, setIsMyCompany] = useState(false);
	const [secondaryActions, setSecondaryActions] = useState([]);
	const hasChildren = Array.isArray(company.companies) && company.companies.length > 0;
	const handleCollapse = () => setCollapsed((prev) => !prev);
	useEffect(() => {
		setIsSelected((company && company.id) === (selectedCompany && selectedCompany.id));
		setIsMyCompany(company && company.id === myCompanyId);
		return () => {
			setIsMyCompany(false);
			setIsSelected(false);
		};
	}, [company, selectedCompany, myCompanyId]);
	useEffect(() => {
		const secActions = [
			{
				id: 1,
				action: (
					<CustomTooltip title={translate("team-management.company-row.title.number-of-active-users")}>
						<span className={styles.row__users}>
							<IconComponent color="currentColor" icon={icon.faUserFriends} size="xs" />
							{company.activeUsers}
						</span>
					</CustomTooltip>
				),
			},
		];
		if (isProjectDirector) {
			secActions.push({
				id: 2,
				action: (
					<CustomIconButton
						icon={icon.faPen}
						size="xs"
						tooltip={translate("team-management.company-row.title.edit")}
						variant="outlined"
						onClick={() => onEdit({ company })}
					/>
				),
			});
		} else if (!isMyCompany && isProjectDirector) {
			secActions.push({
				id: 3,
				action: (
					<CustomIconButton
						icon={company.blocked ? icon.faLock : icon.faLockOpen}
						size="xs"
						tooltip={`${
							company.blocked
								? translate("team-management.company-row.unblock")
								: translate("team-management.company-row.block")
						} ${translate("team-management.company-row.company")}`}
						variant="outlined"
						onClick={() => onBlock({ company })}
					/>
				),
			});
		} else {
			const tooltipTitle = company.blocked
				? translate("team-management.company-row.title.company-is.blocked")
				: translate("team-management.company-row.title.company-is.not-blocked");
			secActions.push({
				id: 4,
				action: (
					<CustomTooltip title={tooltipTitle}>
						<IconComponent
							color="var(--text-secondary)"
							icon={company.blocked ? icon.faLock : icon.faLockOpen}
						/>
					</CustomTooltip>
				),
			});
		}
		setSecondaryActions(secActions);
		return () => setSecondaryActions([]);
	}, [
		company,
		company.activeUsers,
		company.blocked,
		company.privacy,
		displayLeaderIcon,
		isMyCompany,
		isProjectDirector,
		onBlock,
		onEdit,
	]);
	return (
		<>
			<ListItem
				button
				disableGutters
				className={styles.row}
				data-selected={isSelected}
				style={{ paddingLeft: `${8 + 16 * level}px` }}
				onClick={() => onClickCompany(company)}
			>
				<span className={styles.row__info} translate="no">
					<span className={styles.row__toggle}>
						{hasChildren && (
							<CustomTooltip
								title={translate(
									(collapsed && "team-management.company-row.toolltip.reduce") ||
										"team-management.company-row.toolltip.enlarge"
								)}
							>
								<button
									className={styles.row__toggleBtn}
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										handleCollapse();
									}}
								>
									<IconComponent
										color="var(--text-primary)"
										icon={(collapsed && icon.faChevronDown) || icon.faChevronRight}
									/>
								</button>
							</CustomTooltip>
						)}
					</span>
					<CustomTooltip autoTranslate="no" placement="right" title={company.name}>
						<span className={styles.row__name} data-mine={isMyCompany}>
							{company.name}
						</span>
					</CustomTooltip>
					{displayLeaderIcon && (
						<CustomTooltip title={translate("team-management.company-row.title.company-leader")}>
							<span className={styles.row__leader}>
								<IconComponent color="var(--status-info-icon)" icon={icon.faStar} />
							</span>
						</CustomTooltip>
					)}
				</span>
				<span className={styles.row__actions}>
					{secondaryActions.map((secondaryAction) => (
						<Fragment key={secondaryAction.id}>{secondaryAction.action}</Fragment>
					))}
				</span>
			</ListItem>
			{hasChildren && (
				<Collapse unmountOnExit in={collapsed} timeout="auto">
					<List dense disablePadding>
						{company.companies.map((cny) => (
							<CompanyRow
								key={cny.id}
								company={cny}
								displayLeaderIcon={cny.role === Permissions.PROJECT_LEADER.role}
								isProjectDirector={isProjectDirector}
								level={level + 1}
								selectedCompany={selectedCompany}
								onBlock={onBlock}
								onClickCompany={onClickCompany}
								onEdit={onEdit}
							/>
						))}
					</List>
				</Collapse>
			)}
		</>
	);
};

export default CompanyRow;
